// client/src/components/event/ContestConfig.jsx
/**
 * =================================================================
 * Contest / Giveaway Configuration Component
 * =================================================================
 * Follows AuctionConfig pattern for consistency.
 * FEATURES:
 * 1. Set entry requirements, prize details, number of winners
 * 2. Schedule start/end date and time
 * 3. Display shareable hyperlink
 * 4. View entries and statistics
 * 5. Manual winner selection (random draw)
 * =================================================================
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import DateTimeSelect from "../DateTimeSelect";
import "./AuctionConfig.css";

const ContestConfig = ({ tenantId, eventId, itemId, itemName, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const [config, setConfig] = useState({
    name: itemName || "Contest",
    description: "",
    prize_details: "",
    entry_requirements: "",
    winners_to_pick: 1,
    start_at: "",
    end_at: "",
    status: "NEW",
  });

  const [entries, setEntries] = useState([]);
  const [winners, setWinners] = useState([]);
  const [showEntries, setShowEntries] = useState(false);
  const [showWinners, setShowWinners] = useState(false);
  const [entryCount, setEntryCount] = useState(0);
  const [contestExists, setContestExists] = useState(false);

  const baseUrl = process.env.REACT_APP_BASE_URL || "";
  const shareLink = contestExists
    ? `${window.location.origin}/${tenantId}/${eventId}/${itemId}/action?type=contest`
    : "";

  useEffect(() => {
    loadConfig();
  }, [tenantId, eventId, itemId]);

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return "";
    }
  };

  const formatDateTimeForBackend = (val) => {
    if (!val) return null;
    try {
      const d = new Date(val);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    } catch {
      return null;
    }
  };

  const loadConfig = async () => {
    setLoading(true);
    setMessage({ text: "", type: "" });

    try {
      const response = await axios.get(
        `${baseUrl}/api/contest/${tenantId}/event/${eventId}/item/${itemId}/config`
      );

      if (response.data.success && response.data.contest) {
        const c = response.data.contest;
        setContestExists(true);
        setConfig({
          name: c.name || itemName || "Contest",
          description: c.description || "",
          prize_details: c.prize_details || "",
          entry_requirements: c.entry_requirements || "",
          winners_to_pick: c.winners_to_pick || 1,
          start_at: formatDateForInput(c.start_at),
          end_at: formatDateForInput(c.end_at),
          status: c.status || "NEW",
        });

        if (c.status === "COMPLETE") {
          loadWinners(false);
        }

        loadEntryCount();
      } else {
        setContestExists(false);
        setMessage({
          text: "New contest: fill in the form below, then save.",
          type: "info",
        });
      }
    } catch (err) {
      console.error("Error loading contest config:", err);
      if (err.response?.status === 404) {
        setMessage({
          text: "New contest: fill in the form below, then save.",
          type: "info",
        });
      } else {
        setMessage({ text: "Failed to load contest configuration", type: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const loadEntryCount = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/contest/${tenantId}/event/${eventId}/item/${itemId}/entries`
      );
      if (response.data.success) {
        setEntryCount(response.data.count || response.data.entries?.length || 0);
      }
    } catch {
      // Non-critical
    }
  };

  const loadEntries = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/contest/${tenantId}/event/${eventId}/item/${itemId}/entries`
      );
      if (response.data.success) {
        setEntries(response.data.entries);
        setEntryCount(response.data.count || response.data.entries.length);
        setShowEntries(true);
      }
    } catch (err) {
      console.error("Error loading entries:", err);
      setMessage({ text: "Failed to load entries", type: "error" });
    }
  };

  // show=false fetches the list without popping the Winners dialog over the
  // config (used on load for a completed action; buttons pass a click event).
  const loadWinners = async (show = true) => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/contest/${tenantId}/event/${eventId}/item/${itemId}/winners`
      );
      if (response.data.success) {
        setWinners(response.data.winners);
        if (show) setShowWinners(true);
      }
    } catch (err) {
      console.error("Error loading winners:", err);
      setMessage({ text: "Failed to load winners", type: "error" });
    }
  };

  const handleInputChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const validateConfig = () => {
    const errors = [];
    if (!config.name || config.name.trim().length === 0) {
      errors.push("Contest name is required");
    }
    if (config.winners_to_pick < 1 || config.winners_to_pick > 100) {
      errors.push("Winners to pick must be between 1 and 100");
    }
    if (config.start_at && config.end_at) {
      if (new Date(config.end_at) <= new Date(config.start_at)) {
        errors.push("End date must be after start date");
      }
    }
    return errors;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const errors = validateConfig();
    if (errors.length > 0) {
      setMessage({ text: errors.join(". "), type: "error" });
      return;
    }

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const payload = {
        name: config.name,
        description: config.description || null,
        prize_details: config.prize_details || null,
        entry_requirements: config.entry_requirements || null,
        winners_to_pick: Number(config.winners_to_pick),
        start_at: formatDateTimeForBackend(config.start_at),
        end_at: formatDateTimeForBackend(config.end_at),
        status: config.status,
      };

      const response = await axios.post(
        `${baseUrl}/api/contest/${tenantId}/event/${eventId}/item/${itemId}/config`,
        payload
      );

      if (response.data.success) {
        setContestExists(true);
        setMessage({ text: "Contest configuration saved successfully!", type: "success" });
        setTimeout(() => {
          setMessage({ text: "", type: "" });
          loadConfig();
        }, 2000);
      }
    } catch (err) {
      console.error("Error saving contest config:", err);
      setMessage({
        text: err.response?.data?.message || "Failed to save contest configuration",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const copyShareLink = () => {
    if (!shareLink) return;
    navigator.clipboard.writeText(shareLink).then(
      () => {
        setMessage({ text: "Link copied to clipboard!", type: "success" });
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      },
      () => setMessage({ text: "Failed to copy link", type: "error" })
    );
  };

  const handleAwardWinners = async () => {
    if (!window.confirm("Award winners now? This will randomly select winners from the entries.")) {
      return;
    }

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const response = await axios.post(
        `${baseUrl}/api/contest/${tenantId}/event/${eventId}/item/${itemId}/award`
      );
      if (response.data.success) {
        setMessage({
          text: `${response.data.winners?.length || 0} winner(s) selected!`,
          type: "success",
        });
        setTimeout(() => {
          loadConfig();
          loadWinners();
        }, 1000);
      }
    } catch (err) {
      console.error("Error awarding winners:", err);
      setMessage({
        text: err.response?.data?.message || "Failed to award winners",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this contest? This cannot be undone.")) return;

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const response = await axios.delete(
        `${baseUrl}/api/contest/${tenantId}/event/${eventId}/item/${itemId}`
      );
      if (response.data.success) {
        setMessage({ text: "Contest deleted successfully", type: "success" });
        setTimeout(() => {
          if (onClose) onClose();
        }, 1500);
      }
    } catch (err) {
      console.error("Error deleting contest:", err);
      setMessage({
        text: err.response?.data?.message || "Failed to delete contest",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const formatActionsCompleted = (actions) => {
    if (!actions) return "—";
    if (typeof actions === "string") {
      try { actions = JSON.parse(actions); } catch { return actions; }
    }
    return Object.entries(actions)
      .map(([key, val]) => `${key}: ${val}`)
      .join(", ");
  };

  if (loading) {
    return (
      <div className="auction-config-container">
        <div className="loading">Loading contest configuration...</div>
      </div>
    );
  }

  const isComplete = config.status === "COMPLETE";

  return (
    <div className="auction-config-container">
      {/* Header */}
      <div className="auction-config-header">
        <h2>Contest Configuration</h2>
        <button className="close-button" onClick={onClose} aria-label="Close">
          &times;
        </button>
      </div>

      {/* Messages */}
      {message.text && (
        <div className={`message message-${message.type}`}>{message.text}</div>
      )}

      {/* Share Link */}
      {shareLink && (
        <div className="share-link-section">
          <h3>Shareable Link</h3>
          <p className="share-link-description">
            Share this link with participants to enter the contest:
          </p>
          <div className="share-link-container">
            <div className="share-link-display">
              <a href={shareLink} target="_blank" rel="noopener noreferrer">
                {(config.name || itemName || "").replace(/\s+/g, "_")}
              </a>
            </div>
            <button type="button" className="copy-link-button" onClick={copyShareLink}>
              Copy Link
            </button>
          </div>
        </div>
      )}

      {/* Statistics */}
      {contestExists && (
        <div className="auction-stats-section">
          <h3>Contest Statistics</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-label">Total Entries</div>
              <div className="stat-value">{entryCount}</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Winners to Pick</div>
              <div className="stat-value">{config.winners_to_pick}</div>
            </div>
          </div>
          <div className="stats-actions">
            <button type="button" className="view-entries-button" onClick={loadEntries}>
              View All Entries
            </button>
            {isComplete && (
              <button type="button" className="view-winners-button" onClick={loadWinners}>
                View Winners
              </button>
            )}
          </div>
        </div>
      )}

      {/* Configuration Form */}
      <form className="auction-config-form" onSubmit={handleSave}>
        <div className="form-group">
          <label htmlFor="name">
            Contest Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            value={config.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            placeholder="Enter contest name"
            disabled={isComplete}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            value={config.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            placeholder="Describe the contest rules"
            disabled={isComplete}
            rows={3}
          />
        </div>

        <div className="form-group">
          <label htmlFor="prize_details">Prize Details</label>
          <textarea
            id="prize_details"
            value={config.prize_details}
            onChange={(e) => handleInputChange("prize_details", e.target.value)}
            placeholder="Describe the prizes for winners"
            disabled={isComplete}
            rows={2}
          />
        </div>

        <div className="form-group">
          <label htmlFor="entry_requirements">Entry Requirements</label>
          <textarea
            id="entry_requirements"
            value={config.entry_requirements}
            onChange={(e) => handleInputChange("entry_requirements", e.target.value)}
            placeholder="e.g., Follow our page, like this post, tag 2 friends"
            disabled={isComplete}
            rows={2}
          />
          <span className="form-hint">
            List the actions participants must complete to enter
          </span>
        </div>

        <div className="form-group">
          <label htmlFor="winners_to_pick">
            Number of Winners <span className="required">*</span>
          </label>
          <input
            type="number"
            id="winners_to_pick"
            min="1"
            max="100"
            value={config.winners_to_pick}
            onChange={(e) => handleInputChange("winners_to_pick", parseInt(e.target.value) || 1)}
            disabled={isComplete}
            required
          />
          <span className="form-hint">Number of winners to randomly select</span>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="start_at">Start Date & Time</label>
            <DateTimeSelect
              id="start_at"
              value={config.start_at}
              onChange={(v) => handleInputChange("start_at", v)}
              tenantId={tenantId}
              disabled={isComplete}
            />
            <span className="form-hint">When the contest opens</span>
          </div>
          <div className="form-group">
            <label htmlFor="end_at">End Date & Time</label>
            <DateTimeSelect
              id="end_at"
              value={config.end_at}
              onChange={(v) => handleInputChange("end_at", v)}
              tenantId={tenantId}
              disabled={isComplete}
            />
            <span className="form-hint">When the contest closes</span>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="status">Status</label>
          <select
            id="status"
            value={config.status}
            onChange={(e) => handleInputChange("status", e.target.value)}
            disabled={isComplete}
          >
            <option value="NEW">New</option>
            <option value="ACTIVE">Active</option>
            <option value="PROCESSING">Processing</option>
            <option value="COMPLETE">Complete</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>

        <div className="form-actions">
          {isComplete ? (
            <div className="completion-notice">
              Contest is complete - no further changes allowed
            </div>
          ) : (
            <>
              <button type="submit" className="save-button" disabled={saving}>
                {saving ? "Saving..." : "Save Configuration"}
              </button>
              {contestExists && entryCount > 0 && (
                <button
                  type="button"
                  className="award-button"
                  onClick={handleAwardWinners}
                  disabled={saving}
                >
                  Award Winners
                </button>
              )}
              {contestExists && (
                <button
                  type="button"
                  className="delete-button"
                  onClick={handleDelete}
                  disabled={saving}
                >
                  Delete Contest
                </button>
              )}
            </>
          )}
        </div>
      </form>

      {/* Entries Modal */}
      {showEntries && (
        <div className="modal-overlay" onClick={() => setShowEntries(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>All Entries ({entries.length})</h3>
              <button className="close-button" onClick={() => setShowEntries(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <table className="entries-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Actions</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry, index) => (
                    <tr key={entry.id}>
                      <td>{index + 1}</td>
                      <td>{entry.name}</td>
                      <td>{entry.email}</td>
                      <td>{entry.phone || "—"}</td>
                      <td>{formatActionsCompleted(entry.actions_completed)}</td>
                      <td>{new Date(entry.created_on).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Winners Modal */}
      {showWinners && (
        <div className="modal-overlay" onClick={() => setShowWinners(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Winners ({winners.length})</h3>
              <button className="close-button" onClick={() => setShowWinners(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <table className="winners-table">
                <thead>
                  <tr>
                    <th>Place</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                  </tr>
                </thead>
                <tbody>
                  {winners.map((winner, index) => (
                    <tr key={winner.id} className="winner-row">
                      <td className="place-cell">
                        {index === 0 ? "1st" : index === 1 ? "2nd" : index === 2 ? "3rd" : `${index + 1}th`}
                      </td>
                      <td>{winner.name}</td>
                      <td>{winner.email}</td>
                      <td>{winner.phone || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContestConfig;
